"use client";
import { Controller, FieldValues } from "react-hook-form";
import { FormInputControllerProps } from "./Common/FormInputControllerProps";
import { Radio } from "../Inputs/Radio";
import {
  RadioGroupProps,
  RadioProps as MuiRadioProps,
  FormControlProps,
  SxProps,
  Theme,
} from "@mui/material";
import { getFormError } from "./Common/utils";

interface RadioInputProps<T extends FieldValues, Option, ControllerValue>
  extends FormInputControllerProps<T> {
  options: Option[];
  helperText?: string;
  getOptionLabel: (o: Option) => string;
  isOptionEqualToValue: (o: Option, v?: ControllerValue) => boolean;
  mapToControllerValue: (o?: Option) => ControllerValue;
  RadioGroupProps?: Omit<RadioGroupProps, "value" | "onChange">;
  MuiRadioProps?: MuiRadioProps;
  FormControlProps?: FormControlProps;
  sx?: SxProps<Theme>;
}

export function RadioInput<T extends FieldValues, Option, ControllerValue>(
  props: RadioInputProps<T, Option, ControllerValue>,
) {
  return (
    <Controller
      name={props.propertyName}
      control={props.control}
      render={({
        field: { onChange, value },
        fieldState,
        formState: { errors },
      }) => {
        const formError = getFormError(props.propertyName, errors);

        return (
          <Radio
            sx={props.sx}
            helperText={formError ?? props.helperText}
            onChange={(o) => onChange(props.mapToControllerValue(o))}
            value={value}
            isOptionEqualToValue={props.isOptionEqualToValue}
            getOptionLabel={props.getOptionLabel}
            options={props.options}
            MuiRadioProps={props.MuiRadioProps}
            RadioGroupProps={props.RadioGroupProps}
            FormControlProps={{ ...props.FormControlProps, error: !!formError }}
          />
        );
      }}
    />
  );
}
